<template>
  <div>

    <template v-if="contest !== null">
      <table class="prop-table mb-1">
        <tr>
          <th>Type</th>
          <td>{{contestTypeName}}</td>
        </tr>
        <tr>
          <th>Segment</th>
          <td>{{contest.playerGroupId | getPlayerGroupNameVerbose}}</td>
        </tr>
        <tr v-if="isClientMinVersionSupported">
          <th>Client min version</th>
          <td>{{contest.clientMinVersionCode | convertOptionalVersionCodeToStr('not specified')}}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td :class="getContestStatusClass(contest.status)">
            {{contest.status | getEnumTmntStatusNameFormatted}}
          </td>
        </tr>
        <tr>
          <th>Created at</th>
          <td>{{contest.ctime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Planned start</th>
          <td>{{contest.stime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real start</th>
          <td>{{contest.srtime | getContestRealStartTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned duration</th>
          <td>{{plannedDurationStr}}</td>
        </tr>
        <tr>
          <th>Remaining time</th>
          <td>{{getRemainingTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned finish</th>
          <td>{{contest.ftime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real finish</th>
          <td>{{contest.frtime | getContestRealFinishTimeStr}}</td>
        </tr>
        <tr>
          <th>Real duration</th>
          <td>{{getTmntRealDurationStrSimple(contest)}}</td>
        </tr>
        <tr v-if="contest.type === CONTEST_TYPE_CHAMPS">
          <th>Arg1</th>
          <td>{{contest.arg1}}</td>
        </tr>
      </table>

      <hr>

      <template v-if="isGroupCreationRuleSupported">
        <group-creation-rule :rule="contest.groupCreationRule"/>
        <hr>
      </template>

      <b-button
        v-if="canEdit"
        :to="{name: 'contest_edit', params: {contestId: this.contestId}}"
        variant="primary"
      >
        Edit
      </b-button>

      <b-button
        v-if="mustShowForceStartButton"
        @click="forceStartContestWithConfirmation()"
      >
        Force start
      </b-button>
    </template>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
/**
 * @typedef { import('../auth.js').Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';
import LoadingIndicator from './LoadingIndicator.vue';
import GroupCreationRule from './GroupCreationRule.vue';

import NowSecMixin from '../NowSecMixin.js';

import { getPlayerGroupNameVerbose } from '../model/event.js';

/**
 * @typedef { import('../model/contest.js').Contest } Contest
 */

import { CONTEST_TYPE_CHAMPS                        } from '../model/contest.js';

import { CONTEST_STATUS_INCOMING                    } from '../model/contest.js';
import { CONTEST_STATUS_ACTIVE                      } from '../model/contest.js';

import { getContestTypeNameFull                     } from '../model/contest.js';
import { getEnumTmntStatusNameFormatted             } from '../model/contest.js';
import { getContestStatusClass                      } from '../model/contest.js';
import { getContestPlannedDurationStr               } from '../model/contest.js';
import { getContestRemainingTimeStr                 } from '../model/contest.js';
import { getContestRealStartTimeStr                 } from '../model/contest.js';
import { getContestRealFinishTimeStr                } from '../model/contest.js';
import { getTmntRealDurationStrSimple               } from '../model/contest.js';
import { isContestEditable                          } from '../model/contest.js';
import { mayManageContests as utilMayManageContests } from '../model/contest.js';
import { isClientMinVersionSupported                } from '../model/contest.js';
import { isGroupCreationRuleSupported               } from '../model/contest.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'ContestInfo',

  components: {
    LoadingIndicator,
    GroupCreationRule,
  },

  mixins: [
    NowSecMixin,
  ],

  props: {
    contestId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      CONTEST_TYPE_CHAMPS,

      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {?Contest} */
      contest: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.contest === null) {
        return `Contest with id ${this.contestId} does not exist.`;
      }
      return null;
    },

    /** @returns {?string} */
    contestTypeName() {
      const contest = this.contest;
      if(contest === null) {
        return null;
      }

      return getContestTypeNameFull(contest.type);
    },

    /** @returns {boolean} */
    isClientMinVersionSupported() {
      const contest = this.contest;
      if(contest === null) {
        return false;
      }

      return isClientMinVersionSupported(contest.type);
    },

    /** @returns {boolean} */
    isGroupCreationRuleSupported() {
      const contest = this.contest;
      if(contest === null) {
        return false;
      }

      return isGroupCreationRuleSupported(contest.type);
    },

    /** @returns {?string} */
    getRemainingTimeStr() {
      const contest = this.contest;
      if(contest === null) {
        return null;
      }

      return getContestRemainingTimeStr(contest, this.nowSec);
    },

    /** @returns {?string} */
    plannedDurationStr() {
      const contest = this.contest;
      if(contest === null) {
        return null;
      }

      return getContestPlannedDurationStr(contest);
    },

    /** @returns {boolean} */
    canEdit() {
      if(!this.mayManageContests) {
        return false;
      }

      const contest = this.contest;
      if(contest === null) {
        return false; // data not loaded
      }
      return isContestEditable(contest.status);
    },

    /** @returns {boolean} */
    mustShowForceStartButton() {
      if(!this.mayManageContests) {
        return false;
      }

      const contest = this.contest;
      if(contest === null) {
        return false; // data not loaded
      }

      return contest.status === CONTEST_STATUS_INCOMING;
    },

    /** @returns {boolean} */
    mayManageContests() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayManageContests(actor.role);
    },
  },

  created() {
    this.loadContest();
  },

  methods: {
    loadContest() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/contest/${this.contestId}/info`;
      HTTP.get(url)
      .then(response => {
        this.contest = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    forceStartContestWithConfirmation() {
      this.confirmTmntForceStart(() => {
        this.forceStartTmnt();
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmTmntForceStart(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to force start this contest {env}?',
        'Force start',
        onConfirmed,
      );
    },

    forceStartTmnt() {
      const contest = this.contest;
      if(contest === null) {
        notifier.showError(`Contest not loaded.`, {sender: this});
        return;
      }

      notifier.deleteSenderNotifications(this);

      const url = `/contest/${contest.id}/force_start`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        contest.srtime = data.srtime;
        contest.status = CONTEST_STATUS_ACTIVE;
        notifier.showSuccess('Contest started.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    getContestStatusClass,
    getTmntRealDurationStrSimple,
  },

  filters: {
    getPlayerGroupNameVerbose,
    getEnumTmntStatusNameFormatted,
    getContestRealStartTimeStr,
    getContestRealFinishTimeStr,
  },
};
</script>
